import { useState, useEffect, useCallback, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// ═══════════════════════════════════════════════
// SUPABASE CLIENT
// ═══════════════════════════════════════════════
const supabase = createClient(
  "https://aezknrjywaxechqhbjec.supabase.co",
  "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFlemtucmp5d2F4ZWNocWhiamVjIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NzIxOTk2MzUsImV4cCI6MjA4Nzc3NTYzNX0.KmdQ03Kbc2r9FuWahdRxgnKNfs66FVoze-YuARdmP-4"
);

// ═══ HELPERS ═══
const calcSDU = (oz, abv) => (oz * (abv / 100)) / 0.6;
const PRESETS = [
  { id: "beer", label: "Beer", sub: "12 oz · 4.2%", oz: 12, abv: 4.2, emoji: "🍺" },
  { id: "wine", label: "Wine", sub: "5 oz · 12%", oz: 5, abv: 12, emoji: "🍷" },
  { id: "shot", label: "Shot", sub: "1.5 oz · 33%", oz: 1.5, abv: 33, emoji: "🥃" },
];
const BEER_PRESETS = [
  { id: "coors-light", label: "Coors Light", oz: 12, abv: 4.2 },
  { id: "coors-banquet", label: "Coors Banquet", oz: 12, abv: 5 },
];
const DRINK_META = {
  beer: { emoji: "🍺", label: "Beer", accent: "var(--amber)" },
  wine: { emoji: "🍷", label: "Wine", accent: "var(--purple)" },
  shot: { emoji: "🥃", label: "Shot", accent: "var(--red)" },
  custom: { emoji: "🍹", label: "Custom", accent: "var(--blue)" },
};
const genCode = () => {
  const l = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  return Array.from({ length: 5 }, () => l[Math.floor(Math.random() * l.length)]).join("");
};
const fmtTime = (ts) => {
  const d = new Date(ts);
  let h = d.getHours(), m = d.getMinutes();
  const a = h >= 12 ? "PM" : "AM";
  h = h % 12 || 12;
  return `${h}:${m.toString().padStart(2, "0")} ${a}`;
};
const fmtAgo = (ts) => {
  const m = Math.round((Date.now() - new Date(ts).getTime()) / 60000);
  if (m < 1) return "Just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
};

// ═══ CSS ═══
const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800;900&display=swap');
:root {
  --bg: #0B0E13; --surface: #141820; --surface2: #1C2130; --surface3: #242A3A;
  --border: #1E2436; --border2: #2A3148;
  --text: #E8ECF4; --text2: #8B95AB; --text3: #505A72;
  --amber: #F5A623; --purple: #A87FFC; --red: #F45B69; --blue: #4DA6FF;
  --green: #34D399; --teal: #2DD4BF;
  --font: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
}
* { box-sizing: border-box; margin: 0; padding: 0; -webkit-tap-highlight-color: transparent; }
body { background: var(--bg); font-family: var(--font); color: var(--text); overflow-x: hidden; }
@keyframes fadeUp { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
@keyframes pop { 0% { transform: scale(1); } 50% { transform: scale(1.08); } 100% { transform: scale(1); } }
`;

// ═══ SHARED COMPONENTS ═══
function Toast({ msg, show }) {
  return (
    <div
      style={{
        position: "fixed", top: 60, left: "50%",
        transform: `translateX(-50%) translateY(${show ? 0 : -20}px)`,
        background: "var(--surface2)", color: "var(--text)",
        padding: "12px 24px", borderRadius: 100, fontSize: 15, fontWeight: 600,
        border: "1px solid var(--border2)",
        boxShadow: "0 8px 32px rgba(0,0,0,0.4)",
        opacity: show ? 1 : 0,
        transition: "all 0.3s cubic-bezier(.4,0,.2,1)",
        zIndex: 9998, pointerEvents: "none", backdropFilter: "blur(16px)",
      }}
    >
      {msg}
    </div>
  );
}

function Btn({ children, onClick, variant = "primary", disabled = false, style = {} }) {
  const [p, setP] = useState(false);
  const vars = {
    primary: { bg: "var(--amber)", color: "#0B0E13", border: "none" },
    secondary: { bg: "var(--surface2)", color: "var(--text)", border: "1px solid var(--border2)" },
    ghost: { bg: "transparent", color: "var(--text2)", border: "1px solid var(--border)" },
    danger: { bg: "rgba(244,91,105,0.12)", color: "var(--red)", border: "1px solid rgba(244,91,105,0.2)" },
    purple: { bg: "var(--purple)", color: "#fff", border: "none" },
  };
  const v = vars[variant] || vars.primary;
  return (
    <button
      onClick={onClick} disabled={disabled}
      onPointerDown={() => setP(true)} onPointerUp={() => setP(false)} onPointerLeave={() => setP(false)}
      style={{
        background: v.bg, color: v.color, border: v.border, borderRadius: 14,
        padding: "16px 24px", fontSize: 16, fontWeight: 700, fontFamily: "var(--font)",
        cursor: disabled ? "not-allowed" : "pointer", width: "100%",
        transition: "all 0.15s cubic-bezier(.4,0,.2,1)",
        transform: p ? "scale(0.97)" : "scale(1)",
        opacity: disabled ? 0.35 : 1, letterSpacing: "-0.01em", ...style,
      }}
    >
      {children}
    </button>
  );
}

function Card({ children, style = {}, onClick }) {
  return (
    <div
      onClick={onClick}
      style={{
        background: "var(--surface)", borderRadius: 16,
        border: "1px solid var(--border)", padding: "16px 18px",
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s", ...style,
      }}
    >
      {children}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div
      style={{
        fontSize: 12, fontWeight: 700, color: "var(--text3)",
        textTransform: "uppercase", letterSpacing: "0.08em",
        marginBottom: 10, paddingLeft: 2,
      }}
    >
      {children}
    </div>
  );
}

function StatBox({ label, value, color = "var(--text)" }) {
  return (
    <div
      style={{
        flex: 1, background: "var(--surface)", borderRadius: 14,
        padding: "14px 12px", border: "1px solid var(--border)", textAlign: "center",
      }}
    >
      <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 800, color, letterSpacing: "-0.02em" }}>{value}</div>
    </div>
  );
}

// ═══ AUTH SCREEN — Username + Password ═══
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("welcome"); // "welcome" | "login" | "signup"
  const [username, setUsername] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const reset = () => { setUsername(""); setDisplayName(""); setPassword(""); setConfirmPw(""); setError(""); };

  const handleLogin = async () => {
    if (!username.trim() || !password) return;
    setLoading(true);
    setError("");
    try {
      const { data, error: e } = await supabase.auth.signInWithPassword({
        email: `${username.trim().toLowerCase()}@partybeers.local`,
        password,
      });
      if (e) throw new Error("Invalid username or password");
      let { data: profile } = await supabase
        .from("profiles")
        .select("name, username")
        .eq("id", data.user.id)
        .single();
      if (!profile) {
        await supabase.from("profiles").insert({ id: data.user.id, name: username.trim(), username: username.trim().toLowerCase() });
        profile = { name: username.trim() };
      } else if (!profile.username) {
        await supabase.from("profiles").update({ username: username.trim().toLowerCase() }).eq("id", data.user.id);
      }
      onAuth({ id: data.user.id, name: profile.name });
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const handleSignup = async () => {
    if (!username.trim() || !displayName.trim() || !password) return;
    if (password !== confirmPw) { setError("Passwords don't match"); return; }
    if (password.length < 6) { setError("Password must be at least 6 characters"); return; }
    setLoading(true);
    setError("");
    try {
      const { data, error: e } = await supabase.auth.signUp({
        email: `${username.trim().toLowerCase()}@partybeers.local`,
        password,
      });
      if (e) throw e;
      if (!data.user?.identities?.length) throw new Error("Username already taken");
      // Sign in to establish session before profile insert (signUp doesn't always set auth.uid() immediately for RLS)
      await supabase.auth.signInWithPassword({
        email: `${username.trim().toLowerCase()}@partybeers.local`,
        password,
      });
      await supabase.from("profiles").upsert({ id: data.user.id, name: displayName.trim(), username: username.trim().toLowerCase() });
      onAuth({ id: data.user.id, name: displayName.trim() });
    } catch (e) {
      setError(e.message);
    }
    setLoading(false);
  };

  const inputSt = {
    background: "var(--surface)", border: "1px solid var(--border2)", borderRadius: 12,
    color: "var(--text)", fontSize: 17, fontWeight: 500, fontFamily: "var(--font)",
    padding: "16px 18px", width: "100%", outline: "none", boxSizing: "border-box",
    transition: "border-color 0.2s",
  };
  const focusAmber = (e) => (e.target.style.borderColor = "var(--amber)");
  const blurBorder = (e) => (e.target.style.borderColor = "var(--border2)");

  return (
    <div style={{ minHeight: "100dvh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 28 }}>
      <div style={{ textAlign: "center", marginBottom: 48 }}>
        <div style={{ fontSize: 56, marginBottom: 12 }}>🍻</div>
        <h1 style={{ fontSize: 32, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.03em", marginBottom: 4 }}>PartyBeers</h1>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text3)", letterSpacing: "0.12em", textTransform: "uppercase" }}>.live</div>
      </div>

      <div style={{ width: "100%", maxWidth: 360, display: "flex", flexDirection: "column", gap: 14 }}>
        {mode === "welcome" && (
          <>
            <Btn onClick={() => { reset(); setMode("login"); }}>Log In</Btn>
            <Btn onClick={() => { reset(); setMode("signup"); }} variant="secondary">Create Account</Btn>
          </>
        )}

        {mode === "login" && (
          <>
            <SectionLabel>Log in</SectionLabel>
            <input
              type="text" autoCapitalize="none" value={username}
              placeholder="Username"
              onChange={(e) => setUsername(e.target.value)}
              style={inputSt} onFocus={focusAmber} onBlur={blurBorder}
              onKeyDown={(e) => e.key === "Enter" && password && handleLogin()}
            />
            <input
              type="password" value={password}
              placeholder="Password"
              onChange={(e) => setPassword(e.target.value)}
              style={inputSt} onFocus={focusAmber} onBlur={blurBorder}
              onKeyDown={(e) => e.key === "Enter" && username.trim() && handleLogin()}
            />
            {error && <div style={{ color: "var(--red)", fontSize: 14, fontWeight: 600, textAlign: "center" }}>{error}</div>}
            <Btn onClick={handleLogin} disabled={!username.trim() || !password || loading}>
              {loading ? "Logging in..." : "Log In"}
            </Btn>
            <button
              onClick={() => { reset(); setMode("welcome"); }}
              style={{ background: "none", border: "none", color: "var(--text3)", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "12px 0", fontFamily: "var(--font)" }}
            >
              ← Back
            </button>
          </>
        )}

        {mode === "signup" && (
          <>
            <SectionLabel>Create account</SectionLabel>
            <input
              type="text" autoCapitalize="none" value={username}
              placeholder="Username (for logging in)"
              onChange={(e) => setUsername(e.target.value)}
              style={inputSt} onFocus={focusAmber} onBlur={blurBorder}
            />
            <input
              type="text" value={displayName}
              placeholder="Display name (shown to others)"
              onChange={(e) => setDisplayName(e.target.value)}
              style={inputSt} onFocus={focusAmber} onBlur={blurBorder}
            />
            <input
              type="password" value={password}
              placeholder="Password"
              onChange={(e) => setPassword(e.target.value)}
              style={inputSt} onFocus={focusAmber} onBlur={blurBorder}
            />
            <input
              type="password" value={confirmPw}
              placeholder="Confirm password"
              onChange={(e) => setConfirmPw(e.target.value)}
              style={inputSt} onFocus={focusAmber} onBlur={blurBorder}
              onKeyDown={(e) => e.key === "Enter" && handleSignup()}
            />
            {error && <div style={{ color: "var(--red)", fontSize: 14, fontWeight: 600, textAlign: "center" }}>{error}</div>}
            <Btn onClick={handleSignup} disabled={!username.trim() || !displayName.trim() || !password || !confirmPw || loading}>
              {loading ? "Creating account..." : "Create Account"}
            </Btn>
            <button
              onClick={() => { reset(); setMode("welcome"); }}
              style={{ background: "none", border: "none", color: "var(--text3)", fontSize: 14, fontWeight: 600, cursor: "pointer", padding: "12px 0", fontFamily: "var(--font)" }}
            >
              ← Back
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ═══ EVENT HUB ═══
function EventHub({ user, events, onEnter, onCreate, onJoin, onAccount }) {
  const [joinMode, setJoinMode] = useState(false);
  const [code, setCode] = useState("");
  const [joinErr, setJoinErr] = useState("");
  const [joining, setJoining] = useState(false);

  const handleJoin = async () => {
    setJoining(true);
    setJoinErr("");
    try {
      const { data: event, error: e } = await supabase
        .from("events")
        .select("id")
        .eq("code", code.trim().toUpperCase())
        .single();
      if (e || !event) throw new Error("Event not found — check the code and try again");
      onJoin(event.id);
      setCode("");
      setJoinMode(false);
    } catch (e) {
      setJoinErr(e.message);
    }
    setJoining(false);
  };

  return (
    <div style={{ minHeight: "100dvh", padding: "20px 20px 120px" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 28, paddingTop: 8 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Welcome back</div>
          <div style={{ fontSize: 26, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.02em", marginTop: 2 }}>{user.name}</div>
        </div>
        <button onClick={onAccount} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, width: 40, height: 40, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 18, color: "var(--text2)" }}>⚙</button>
      </div>

      <SectionLabel>Your Events</SectionLabel>
      {events.length === 0 ? (
        <Card style={{ textAlign: "center", padding: "40px 20px" }}>
          <div style={{ fontSize: 36, marginBottom: 10 }}>🍺</div>
          <div style={{ color: "var(--text3)", fontSize: 15, fontWeight: 600 }}>No events yet</div>
        </Card>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 20 }}>
          {events.map((ev) => (
            <Card key={ev.id} onClick={() => onEnter(ev.id)} style={{ cursor: "pointer" }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)", marginBottom: 3 }}>{ev.name}</div>
                  <div style={{ fontSize: 13, fontWeight: 500, color: "var(--text3)" }}>
                    {ev.member_count || 0} people · <span style={{ color: "var(--amber)", fontWeight: 700, letterSpacing: "0.08em" }}>{ev.code}</span>
                  </div>
                </div>
                <div style={{ color: "var(--text3)", fontSize: 20 }}>›</div>
              </div>
              <div style={{ display: "flex", gap: 16 }}>
                <span style={{ fontSize: 13, color: "var(--text3)" }}>You <span style={{ color: "var(--amber)", fontWeight: 800, fontSize: 15 }}>{(ev.my_units || 0).toFixed(1)}</span></span>
                <span style={{ fontSize: 13, color: "var(--text3)" }}>Total <span style={{ color: "var(--purple)", fontWeight: 800, fontSize: 15 }}>{(ev.total_units || 0).toFixed(1)}</span></span>
              </div>
            </Card>
          ))}
        </div>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16 }}>
        <Btn onClick={onCreate} variant="purple">+ Create Event</Btn>
        {!joinMode ? (
          <Btn onClick={() => setJoinMode(true)} variant="secondary">Join with Code</Btn>
        ) : (
          <Card style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <input
              type="text" value={code} placeholder="ABCDE" maxLength={5}
              onChange={(e) => setCode(e.target.value.toUpperCase().replace(/[^A-Z]/g, "").slice(0, 5))}
              style={{ background: "var(--bg)", border: "1px solid var(--border2)", borderRadius: 10, color: "var(--text)", fontSize: 26, fontWeight: 800, padding: "14px 16px", width: "100%", textAlign: "center", outline: "none", letterSpacing: "0.3em", boxSizing: "border-box", fontFamily: "var(--font)" }}
              autoFocus
            />
            {joinErr && <div style={{ color: "var(--red)", fontSize: 13, fontWeight: 600, textAlign: "center" }}>{joinErr}</div>}
            <div style={{ display: "flex", gap: 10 }}>
              <Btn onClick={() => { setJoinMode(false); setCode(""); setJoinErr(""); }} variant="ghost" style={{ flex: 1 }}>Cancel</Btn>
              <Btn onClick={handleJoin} style={{ flex: 1.5 }} disabled={code.length < 5 || joining}>{joining ? "Joining..." : "Join"}</Btn>
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}

// ═══ CREATE EVENT ═══
function CreateEvent({ user, onSave, onBack }) {
  const [name, setName] = useState("");
  const [code] = useState(genCode);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    setSaving(true);
    setError("");
    try {
      const { data: newEvent, error: e } = await supabase
        .from("events")
        .insert({ name: name.trim(), code, created_by: user.id })
        .select("id")
        .single();
      if (e) throw e;
      if (!newEvent?.id) throw new Error("Event could not be created");
      onSave(newEvent.id);
    } catch (e) {
      setError(e.message || "Failed to create event");
    }
    setSaving(false);
  };

  return (
    <div style={{ minHeight: "100dvh", padding: "20px 20px 40px" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--text3)", fontSize: 15, fontWeight: 600, cursor: "pointer", padding: "8px 0", marginBottom: 24, fontFamily: "var(--font)" }}>← Back</button>
      <h1 style={{ fontSize: 26, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.02em", marginBottom: 32 }}>Create Event</h1>
      <SectionLabel>Event Name</SectionLabel>
      <input
        type="text" value={name} placeholder="e.g. Jake's Birthday Bash"
        onChange={(e) => setName(e.target.value)}
        style={{ background: "var(--surface)", border: "1px solid var(--border2)", borderRadius: 12, color: "var(--text)", fontSize: 17, fontWeight: 500, padding: "16px 18px", width: "100%", outline: "none", boxSizing: "border-box", fontFamily: "var(--font)", marginBottom: 28 }}
        autoFocus
      />
      <SectionLabel>Group Code</SectionLabel>
      <Card style={{ textAlign: "center", padding: "24px 20px", marginBottom: 10, border: "1px solid rgba(245,166,35,0.2)" }}>
        <div style={{ fontSize: 38, fontWeight: 900, color: "var(--amber)", letterSpacing: "0.25em" }}>{code}</div>
      </Card>
      <div style={{ color: "var(--text3)", fontSize: 13, fontWeight: 500, textAlign: "center", marginBottom: 36 }}>Share this code with your group</div>
      {error && <div style={{ color: "var(--red)", fontSize: 14, fontWeight: 600, textAlign: "center", marginBottom: 12 }}>{error}</div>}
      <Btn onClick={handleCreate} disabled={!name.trim() || saving}>{saving ? "Creating..." : "Create Event"}</Btn>
    </div>
  );
}

// ═══ MANAGE EVENT MODAL ═══
function ManageModal({ open, onClose, event, members, onSave, userId, onLeave, onDelete }) {
  const [name, setName] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  useEffect(() => { if (open && event) { setName(event.name); setConfirmDelete(false); } }, [open, event]);
  if (!open || !event) return null;

  const handleSave = async () => {
    if (!name.trim()) return;
    await supabase.from("events").update({ name: name.trim() }).eq("id", event.id);
    onSave();
    onClose();
  };

  const removeMember = async (memberId) => {
    if (memberId === userId) return;
    await supabase.from("event_members").delete().eq("event_id", event.id).eq("user_id", memberId);
    onSave();
  };

  const iSt = { background: "var(--surface)", border: "1px solid var(--border2)", borderRadius: 10, color: "var(--text)", fontSize: 16, fontWeight: 500, padding: "14px 16px", width: "100%", outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" };
  const isAdmin = event.created_by === userId;

  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "flex-start", justifyContent: "center", zIndex: 1000, padding: "80px 20px 20px", backdropFilter: "blur(12px)", overflowY: "auto" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 20, padding: 24, width: "100%", maxWidth: 400, border: "1px solid var(--border2)" }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "var(--text)", marginBottom: 24, textAlign: "center" }}>Manage Event</h2>

        {isAdmin && (
          <>
            <SectionLabel>Name</SectionLabel>
            <input type="text" value={name} onChange={(e) => setName(e.target.value)} style={{ ...iSt, marginBottom: 20 }} />
          </>
        )}

        <SectionLabel>Members ({members.length})</SectionLabel>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 24, maxHeight: 260, overflowY: "auto" }}>
          {members.map((m) => (
            <div key={m.user_id} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: m.user_id === userId ? "rgba(245,166,35,0.06)" : "var(--bg)", borderRadius: 10, border: m.user_id === userId ? "1px solid rgba(245,166,35,0.15)" : "1px solid var(--border)" }}>
              <div>
                <span style={{ fontSize: 15, fontWeight: 600, color: m.user_id === userId ? "var(--amber)" : "var(--text)" }}>{m.name}</span>
                {m.role === "admin" && <span style={{ fontSize: 11, color: "var(--purple)", fontWeight: 700, marginLeft: 8 }}>ADMIN</span>}
                {m.user_id === userId && <span style={{ fontSize: 11, color: "var(--text3)", marginLeft: 6 }}>(you)</span>}
              </div>
              {isAdmin && m.user_id !== userId && (
                <button onClick={() => removeMember(m.user_id)} style={{ background: "rgba(244,91,105,0.1)", border: "1px solid rgba(244,91,105,0.2)", borderRadius: 6, color: "var(--red)", width: 30, height: 30, fontSize: 14, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center" }}>✕</button>
              )}
            </div>
          ))}
        </div>

        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={onClose} variant="ghost" style={{ flex: 1 }}>Cancel</Btn>
          {isAdmin && <Btn onClick={handleSave} variant="purple" style={{ flex: 1.5 }} disabled={!name.trim()}>Save</Btn>}
        </div>

        {!isAdmin && (
          <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 20 }}>
            <Btn onClick={onLeave} variant="danger">Leave Event</Btn>
          </div>
        )}

        {isAdmin && (
          <div style={{ marginTop: 20, borderTop: "1px solid var(--border)", paddingTop: 20 }}>
            {!confirmDelete ? (
              <Btn onClick={() => setConfirmDelete(true)} variant="danger">Delete Event</Btn>
            ) : (
              <Card style={{ display: "flex", flexDirection: "column", gap: 12, border: "1px solid rgba(244,91,105,0.2)" }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", textAlign: "center" }}>Delete this event?</div>
                <div style={{ fontSize: 13, color: "var(--text3)", textAlign: "center" }}>All drinks and members will be permanently removed.</div>
                <div style={{ display: "flex", gap: 10 }}>
                  <Btn onClick={() => setConfirmDelete(false)} variant="ghost" style={{ flex: 1 }}>Cancel</Btn>
                  <Btn onClick={onDelete} variant="danger" style={{ flex: 1 }}>Yes, Delete</Btn>
                </div>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

// ═══ CUSTOM DRINK MODAL ═══
const CUSTOM_CATS = [
  { id: "beer", emoji: "🍺", label: "Beer" },
  { id: "wine", emoji: "🍷", label: "Wine" },
  { id: "shot", emoji: "🥃", label: "Shot" },
  { id: "custom", emoji: "🍹", label: "Other" },
];
function CustomModal({ open, onClose, onLog, initialCategory = "custom" }) {
  const [oz, setOz] = useState("16");
  const [abv, setAbv] = useState("5");
  const [category, setCategory] = useState("custom");
  useEffect(() => { if (open) setCategory(initialCategory); }, [open, initialCategory]);
  if (!open) return null;
  const u = calcSDU(parseFloat(oz) || 0, parseFloat(abv) || 0);
  const iSt = { background: "var(--bg)", border: "1px solid var(--border2)", borderRadius: 10, color: "var(--text)", fontSize: 24, fontWeight: 700, padding: "14px 16px", width: "100%", textAlign: "center", outline: "none", boxSizing: "border-box", fontFamily: "var(--font)" };
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000, padding: 20, backdropFilter: "blur(12px)" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 20, padding: 28, width: "100%", maxWidth: 380, border: "1px solid var(--border2)" }}>
        <h2 style={{ fontSize: 20, fontWeight: 800, color: "var(--text)", margin: "0 0 20px", textAlign: "center" }}>Custom Drink</h2>
        <SectionLabel>Type</SectionLabel>
        <div style={{ display: "flex", gap: 8, marginBottom: 20 }}>
          {CUSTOM_CATS.map((c) => {
            const accent = DRINK_META[c.id].accent;
            const sel = category === c.id;
            return (
              <button key={c.id} onClick={() => setCategory(c.id)} style={{ flex: 1, padding: "10px 0", borderRadius: 10, border: sel ? `2px solid ${accent}` : "1px solid var(--border2)", background: sel ? `${accent}1A` : "var(--bg)", cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", gap: 3, fontFamily: "var(--font)", transition: "all 0.15s" }}>
                <span style={{ fontSize: 20 }}>{c.emoji}</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: sel ? accent : "var(--text3)" }}>{c.label}</span>
              </button>
            );
          })}
        </div>
        <div style={{ marginBottom: 16 }}><SectionLabel>Ounces</SectionLabel><input type="number" inputMode="decimal" value={oz} onChange={(e) => setOz(e.target.value)} style={iSt} min="0" step="0.5" /></div>
        <div style={{ marginBottom: 20 }}><SectionLabel>ABV %</SectionLabel><input type="number" inputMode="decimal" value={abv} onChange={(e) => setAbv(e.target.value)} style={iSt} min="0" step="0.1" max="100" /></div>
        <Card style={{ textAlign: "center", padding: "12px 16px", marginBottom: 24 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Standard Units</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: "var(--blue)" }}>{u.toFixed(2)}</div>
        </Card>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={onClose} variant="ghost" style={{ flex: 1 }}>Cancel</Btn>
          <Btn onClick={() => {
            const ozVal = parseFloat(oz);
            const abvVal = parseFloat(abv);
            if (ozVal > 30) { alert("Custom drinks cannot exceed 30oz"); return; }
            if (abvVal > 50) { alert("Custom drinks cannot exceed 50% ABV"); return; }
            if (calcSDU(ozVal, abvVal) > 8) { alert("Your drink is way too strong!"); return; }
            onLog(ozVal, abvVal, category); onClose();
          }} style={{ flex: 1.5 }} disabled={!oz || !abv || parseFloat(oz) <= 0 || parseFloat(abv) <= 0}>Log It</Btn>
        </div>
      </div>
    </div>
  );
}

// ═══ DELETE CONFIRM ═══
function DeleteConfirm({ open, onClose, onConfirm, drinkType }) {
  if (!open) return null;
  const meta = DRINK_META[drinkType] || DRINK_META.custom;
  return (
    <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1001, padding: 20, backdropFilter: "blur(12px)" }}>
      <div onClick={(e) => e.stopPropagation()} style={{ background: "var(--surface)", borderRadius: 20, padding: 28, width: "100%", maxWidth: 340, border: "1px solid var(--border2)", textAlign: "center" }}>
        <div style={{ fontSize: 40, marginBottom: 12 }}>{meta.emoji}</div>
        <div style={{ fontSize: 18, fontWeight: 700, color: "var(--text)", marginBottom: 8 }}>Remove this drink?</div>
        <div style={{ fontSize: 14, color: "var(--text3)", marginBottom: 24 }}>This can't be undone</div>
        <div style={{ display: "flex", gap: 10 }}>
          <Btn onClick={onClose} variant="ghost" style={{ flex: 1 }}>Cancel</Btn>
          <Btn onClick={() => { onConfirm(); onClose(); }} variant="danger" style={{ flex: 1 }}>Remove</Btn>
        </div>
      </div>
    </div>
  );
}

// ═══ LOG FOR OTHERS ═══
function LogOther({ participant, onLog, onCustom }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen(!open)} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 16px", width: "100%", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "space-between", fontFamily: "var(--font)" }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>{participant.name}</span>
        <span style={{ color: "var(--text3)", fontSize: 18, transition: "transform 0.2s", transform: open ? "rotate(180deg)" : "none" }}>▾</span>
      </button>
      {open && (
        <div style={{ display: "flex", gap: 8, marginTop: 8, paddingLeft: 4 }}>
          {PRESETS.map((p) => (
            <button key={p.id} onClick={() => { onLog(p.oz, p.abv, p.id, participant.user_id); setOpen(false); }} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 10, color: "var(--text)", padding: "10px 0", fontSize: 22, cursor: "pointer", flex: 1, fontFamily: "var(--font)" }}>
              {p.emoji}
            </button>
          ))}
          <button onClick={() => { onCustom(participant.user_id); setOpen(false); }} style={{ background: "var(--surface)", border: "1px dashed var(--border2)", borderRadius: 10, color: "var(--text2)", padding: "10px 0", fontSize: 22, cursor: "pointer", flex: 1, fontFamily: "var(--font)" }}>
            🍹
          </button>
        </div>
      )}
    </div>
  );
}

// ═══ LEADERBOARD ROW ═══
function LBRow({ rank, name, units, isSelf, max, onClick }) {
  const medals = ["🥇", "🥈", "🥉"];
  const w = max > 0 ? (units / max) * 100 : 0;
  return (
    <button onClick={onClick} style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 16px", borderRadius: 14, background: isSelf ? "rgba(245,166,35,0.06)" : "var(--surface)", border: isSelf ? "1px solid rgba(245,166,35,0.15)" : "1px solid var(--border)", width: "100%", cursor: "pointer", position: "relative", overflow: "hidden", transition: "all 0.15s", fontFamily: "var(--font)" }}
      onPointerDown={(e) => (e.currentTarget.style.transform = "scale(0.98)")}
      onPointerUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
      onPointerLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
    >
      <div style={{ position: "absolute", left: 0, top: 0, bottom: 0, width: `${w}%`, background: isSelf ? "rgba(245,166,35,0.04)" : "rgba(255,255,255,0.015)", transition: "width 0.6s ease" }} />
      <div style={{ fontSize: rank < 3 ? 22 : 14, minWidth: 32, textAlign: "center", fontWeight: 800, color: rank >= 3 ? "var(--text3)" : undefined, position: "relative" }}>{rank < 3 ? medals[rank] : `${rank + 1}`}</div>
      <div style={{ flex: 1, fontSize: 16, fontWeight: 600, color: isSelf ? "var(--amber)" : "var(--text)", position: "relative", textAlign: "left" }}>{name}</div>
      <div style={{ fontSize: 20, fontWeight: 800, color: "var(--text)", position: "relative", letterSpacing: "-0.02em" }}>{units.toFixed(1)}</div>
      <div style={{ color: "var(--text3)", fontSize: 16, position: "relative" }}>›</div>
    </button>
  );
}

// ═══ USER DETAIL ═══
function UserDetail({ userId: viewUserId, eventId, currentUserId, isAdmin, onBack, onDrinkRemoved, flash }) {
  const [profile, setProfile] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loggerMap, setLoggerMap] = useState({});
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [{ data: p }, { data: l }] = await Promise.all([
        supabase.from("profiles").select("id, name, username").eq("id", viewUserId).single(),
        supabase.from("drink_logs").select("*").eq("event_id", eventId).eq("user_id", viewUserId).order("created_at", { ascending: false }),
      ]);
      setProfile(p);
      setLogs(l || []);
      // Fetch logger profiles for "Logged by" display
      const loggerIds = [...new Set((l || []).map((log) => log.logged_by).filter((id) => id && id !== viewUserId))];
      if (loggerIds.length > 0) {
        const { data: loggers } = await supabase.from("profiles").select("id, name").in("id", loggerIds);
        const map = {};
        (loggers || []).forEach((lg) => { map[lg.id] = lg; });
        setLoggerMap(map);
      }
      setLoading(false);
    })();
  }, [viewUserId, eventId]);

  const handleRemove = async (logId) => {
    await supabase.from("drink_logs").delete().eq("id", logId);
    setLogs((prev) => prev.filter((l) => l.id !== logId));
    onDrinkRemoved();
    flash("Drink removed");
  };

  if (loading || !profile) return <div style={{ padding: 40, textAlign: "center", color: "var(--text3)" }}>Loading...</div>;

  const isSelf = viewUserId === currentUserId;
  const canDelete = isSelf || isAdmin;
  const tot = logs.reduce((s, l) => s + parseFloat(l.units), 0);
  const byType = {};
  logs.forEach((l) => { if (!byType[l.drink_type]) byType[l.drink_type] = { count: 0, units: 0 }; byType[l.drink_type].count++; byType[l.drink_type].units += parseFloat(l.units); });
  const types = Object.entries(byType).sort((a, b) => b[1].units - a[1].units);
  const first = logs.length > 0 ? logs[logs.length - 1].created_at : null;
  const hrs = first ? Math.max((Date.now() - new Date(first).getTime()) / 36e5, 0.1) : 0;
  const pace = hrs > 0 ? tot / hrs : 0;

  return (
    <div style={{ padding: "20px 20px 40px", animation: "fadeUp 0.3s ease" }}>
      <DeleteConfirm open={!!deleteTarget} onClose={() => setDeleteTarget(null)} onConfirm={() => handleRemove(deleteTarget.id)} drinkType={deleteTarget?.drink_type} />
      <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--text3)", fontSize: 15, fontWeight: 600, cursor: "pointer", padding: "8px 0", marginBottom: 20, fontFamily: "var(--font)" }}>← Leaderboard</button>
      <div style={{ textAlign: "center", marginBottom: 24 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: isSelf ? "var(--amber)" : "var(--text)", letterSpacing: "-0.02em" }}>{profile.name}</h1>
        {profile.username && <div style={{ fontSize: 13, color: "var(--text3)", fontWeight: 500, marginTop: 2 }}>@{profile.username}</div>}
        {isSelf && <div style={{ fontSize: 13, color: "var(--text3)", fontWeight: 500, marginTop: 2 }}>That's you</div>}
      </div>
      <div style={{ display: "flex", gap: 8, marginBottom: 24 }}>
        <StatBox label="Units" value={tot.toFixed(1)} color="var(--amber)" />
        <StatBox label="Drinks" value={logs.length} />
        <StatBox label="Units/Hr" value={pace.toFixed(1)} color={pace > 1.5 ? "var(--red)" : pace > 0.8 ? "var(--amber)" : "var(--green)"} />
      </div>

      {types.length > 0 && (
        <>
          <SectionLabel>Breakdown</SectionLabel>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 24 }}>
            {types.map(([type, d]) => {
              const meta = DRINK_META[type] || DRINK_META.custom;
              const pct = tot > 0 ? Math.round((d.units / tot) * 100) : 0;
              return (
                <Card key={type} style={{ flex: "1 1 calc(50% - 4px)", minWidth: 130, position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", left: 0, bottom: 0, width: `${pct}%`, height: 3, background: meta.accent, opacity: 0.5, borderRadius: "0 2px 0 0" }} />
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}><span style={{ fontSize: 20 }}>{meta.emoji}</span><span style={{ fontSize: 14, fontWeight: 700, color: meta.accent }}>{meta.label}</span></div>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline" }}><span style={{ fontSize: 20, fontWeight: 800, color: "var(--text)" }}>{d.count}×</span><span style={{ fontSize: 12, fontWeight: 600, color: "var(--text3)" }}>{d.units.toFixed(1)}u · {pct}%</span></div>
                </Card>
              );
            })}
          </div>
        </>
      )}

      <SectionLabel>Timeline</SectionLabel>
      {logs.length === 0 ? (
        <div style={{ textAlign: "center", padding: 40, color: "var(--text3)", fontSize: 15, fontWeight: 500 }}>No drinks yet</div>
      ) : (
        <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
          {logs.map((l, i) => {
            const meta = DRINK_META[l.drink_type] || DRINK_META.custom;
            const last = i === logs.length - 1;
            return (
              <div key={l.id} style={{ display: "flex", gap: 12 }}>
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", width: 32, flexShrink: 0 }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: "var(--surface2)", border: "1.5px solid var(--border2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, zIndex: 1 }}>{meta.emoji}</div>
                  {!last && <div style={{ width: 1.5, flex: 1, background: "var(--border)", minHeight: 12 }} />}
                </div>
                <div style={{ flex: 1, paddingBottom: last ? 0 : 14, paddingTop: 3, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>{meta.label}{l.oz && l.abv ? <span style={{ color: "var(--text3)", fontWeight: 500 }}> ({parseFloat(l.oz)}oz, {parseFloat(l.abv)}%)</span> : null} <span style={{ color: meta.accent, fontWeight: 800 }}>+{parseFloat(l.units).toFixed(2)}u</span></div>
                    <div style={{ fontSize: 12, color: "var(--text3)", marginTop: 2 }}>{fmtTime(l.created_at)} · {fmtAgo(l.created_at)}{l.logged_by && l.logged_by !== viewUserId && loggerMap[l.logged_by] && ` · Logged by: ${loggerMap[l.logged_by].name}`}</div>
                  </div>
                  {canDelete && (
                    <button onClick={() => setDeleteTarget(l)} style={{ background: "rgba(244,91,105,0.08)", border: "1px solid rgba(244,91,105,0.15)", borderRadius: 8, color: "var(--red)", width: 32, height: 32, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, marginTop: 2, fontFamily: "var(--font)", fontWeight: 700 }}>✕</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ═══ ACCOUNT SETTINGS ═══
function AccountScreen({ user, onBack, onLogout, onNameChange, flash }) {
  const [displayName, setDisplayName] = useState(user.name);
  const [savingName, setSavingName] = useState(false);
  const [newPw, setNewPw] = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [savingPw, setSavingPw] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const iSt = {
    background: "var(--surface)", border: "1px solid var(--border2)", borderRadius: 12,
    color: "var(--text)", fontSize: 17, fontWeight: 500, fontFamily: "var(--font)",
    padding: "16px 18px", width: "100%", outline: "none", boxSizing: "border-box",
    transition: "border-color 0.2s",
  };
  const focusAmber = (e) => (e.target.style.borderColor = "var(--amber)");
  const blurBorder = (e) => (e.target.style.borderColor = "var(--border2)");

  const handleSaveName = async () => {
    if (!displayName.trim() || displayName.trim() === user.name) return;
    setSavingName(true);
    const { error } = await supabase.from("profiles").update({ name: displayName.trim() }).eq("id", user.id);
    if (error) { flash("Failed to update name"); setSavingName(false); return; }
    onNameChange(displayName.trim());
    flash("Display name updated");
    setSavingName(false);
  };

  const handleChangePassword = async () => {
    if (!newPw || newPw.length < 6) { flash("Password must be at least 6 characters"); return; }
    if (newPw !== confirmPw) { flash("Passwords don't match"); return; }
    setSavingPw(true);
    const { error } = await supabase.auth.updateUser({ password: newPw });
    if (error) { flash("Failed to update password"); setSavingPw(false); return; }
    setNewPw(""); setConfirmPw("");
    flash("Password updated");
    setSavingPw(false);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    await supabase.from("drink_logs").delete().eq("user_id", user.id);
    await supabase.from("event_members").delete().eq("user_id", user.id);
    await supabase.from("profiles").delete().eq("id", user.id);
    flash("Account deleted");
    onLogout();
  };

  return (
    <div style={{ minHeight: "100dvh", padding: "20px 20px 40px", animation: "fadeUp 0.3s ease" }}>
      <button onClick={onBack} style={{ background: "none", border: "none", color: "var(--text3)", fontSize: 15, fontWeight: 600, cursor: "pointer", padding: "8px 0", marginBottom: 20, fontFamily: "var(--font)" }}>← Back</button>
      <div style={{ textAlign: "center", marginBottom: 32 }}>
        <h1 style={{ fontSize: 26, fontWeight: 800, color: "var(--text)", letterSpacing: "-0.02em" }}>{user.name}</h1>
        <div style={{ fontSize: 13, color: "var(--text3)", fontWeight: 500, marginTop: 2 }}>Account</div>
      </div>

      <SectionLabel>Display Name</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 32 }}>
        <input type="text" value={displayName} onChange={(e) => setDisplayName(e.target.value)} style={iSt} onFocus={focusAmber} onBlur={blurBorder} />
        <Btn onClick={handleSaveName} disabled={!displayName.trim() || displayName.trim() === user.name || savingName}>
          {savingName ? "Saving..." : "Save Name"}
        </Btn>
      </div>

      <SectionLabel>Change Password</SectionLabel>
      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginBottom: 32 }}>
        <input type="password" value={newPw} placeholder="New password" onChange={(e) => setNewPw(e.target.value)} style={iSt} onFocus={focusAmber} onBlur={blurBorder} />
        <input type="password" value={confirmPw} placeholder="Confirm new password" onChange={(e) => setConfirmPw(e.target.value)} style={iSt} onFocus={focusAmber} onBlur={blurBorder}
          onKeyDown={(e) => e.key === "Enter" && newPw && confirmPw && handleChangePassword()}
        />
        <Btn onClick={handleChangePassword} disabled={!newPw || !confirmPw || savingPw}>
          {savingPw ? "Updating..." : "Update Password"}
        </Btn>
      </div>

      <div style={{ marginBottom: 32 }}>
        <Btn onClick={onLogout} variant="secondary">Sign Out</Btn>
      </div>

      <SectionLabel>Danger Zone</SectionLabel>
      {!confirmDelete ? (
        <Btn onClick={() => setConfirmDelete(true)} variant="danger">Delete Account</Btn>
      ) : (
        <Card style={{ display: "flex", flexDirection: "column", gap: 12, border: "1px solid rgba(244,91,105,0.2)" }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)", textAlign: "center" }}>Are you sure? This can't be undone.</div>
          <div style={{ fontSize: 13, color: "var(--text3)", textAlign: "center" }}>All your drinks and event memberships will be permanently deleted.</div>
          <div style={{ display: "flex", gap: 10 }}>
            <Btn onClick={() => setConfirmDelete(false)} variant="ghost" style={{ flex: 1 }}>Cancel</Btn>
            <Btn onClick={handleDeleteAccount} variant="danger" style={{ flex: 1 }} disabled={deleting}>{deleting ? "Deleting..." : "Yes, Delete"}</Btn>
          </div>
        </Card>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════
// MAIN APP
// ═══════════════════════════════════════════════
export default function PartyBeers() {
  const [screen, setScreen] = useState("loading");
  const [user, setUser] = useState(null);
  const [events, setEvents] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [activeEvent, setActiveEvent] = useState(null);
  const [members, setMembers] = useState([]);
  const [logs, setLogs] = useState([]);
  const [selUserId, setSelUserId] = useState(null);
  const [customOpen, setCustomOpen] = useState(false);
  const [customTarget, setCustomTarget] = useState(null);
  const [customCategory, setCustomCategory] = useState("custom");
  const [beerOpen, setBeerOpen] = useState(false);
  const [manageOpen, setManageOpen] = useState(false);
  const [toast, setToast] = useState({ msg: "", show: false });
  const tt = useRef(null);

  const flash = useCallback((m) => {
    if (tt.current) clearTimeout(tt.current);
    setToast({ msg: m, show: true });
    tt.current = setTimeout(() => setToast((t) => ({ ...t, show: false })), 2200);
  }, []);

  // ─── INIT: Check session ───
  useEffect(() => {
    (async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session?.user) {
        const { data: profile } = await supabase
          .from("profiles")
          .select("name")
          .eq("id", session.user.id)
          .single();
        if (profile?.name) {
          setUser({ id: session.user.id, name: profile.name });
          setScreen("hub");
          return;
        }
      }
      setScreen("auth");
    })();
  }, []);

  // ─── LOAD USER'S EVENTS ───
  const loadEvents = useCallback(async () => {
    if (!user) return;
    const { data: myMemberships } = await supabase
      .from("event_members")
      .select("event_id")
      .eq("user_id", user.id);

    if (!myMemberships?.length) { setEvents([]); return; }

    const eventIds = myMemberships.map((m) => m.event_id);
    const { data: evts } = await supabase
      .from("events")
      .select("id, name, code, created_by")
      .in("id", eventIds);

    // Get stats for each event
    const enriched = await Promise.all(
      (evts || []).map(async (ev) => {
        const [{ count: mc }, { data: dl }] = await Promise.all([
          supabase.from("event_members").select("*", { count: "exact", head: true }).eq("event_id", ev.id),
          supabase.from("drink_logs").select("user_id, units").eq("event_id", ev.id),
        ]);
        const total_units = (dl || []).reduce((s, l) => s + parseFloat(l.units), 0);
        const my_units = (dl || []).filter((l) => l.user_id === user.id).reduce((s, l) => s + parseFloat(l.units), 0);
        return { ...ev, member_count: mc || 0, total_units, my_units };
      })
    );
    setEvents(enriched);
  }, [user]);

  useEffect(() => { if (user) loadEvents(); }, [user, loadEvents]);

  // Refresh events whenever user navigates back to hub
  useEffect(() => { if (screen === "hub" && user) loadEvents(); }, [screen]);

  // ─── LOAD ACTIVE EVENT DATA ───
  const loadEventData = useCallback(async () => {
    if (!activeId) return;
    const [{ data: ev }, { data: mem }, { data: dl }] = await Promise.all([
      supabase.from("events").select("*").eq("id", activeId).single(),
      supabase.from("event_members").select("user_id, role, profiles(name)").eq("event_id", activeId),
      supabase.from("drink_logs").select("*").eq("event_id", activeId).order("created_at", { ascending: false }),
    ]);
    setActiveEvent(ev);
    setMembers((mem || []).map((m) => ({ user_id: m.user_id, role: m.role, name: m.profiles?.name || "Unknown" })));
    setLogs(dl || []);
  }, [activeId]);

  useEffect(() => { if (activeId) loadEventData(); }, [activeId, loadEventData]);

  // ─── REALTIME: drink_logs ───
  useEffect(() => {
    if (!activeId) return;
    const channel = supabase
      .channel(`event-${activeId}`)
      .on("postgres_changes", { event: "*", schema: "public", table: "drink_logs", filter: `event_id=eq.${activeId}` }, () => {
        loadEventData();
      })
      .on("postgres_changes", { event: "*", schema: "public", table: "event_members", filter: `event_id=eq.${activeId}` }, () => {
        loadEventData();
      })
      .subscribe();
    return () => { supabase.removeChannel(channel); };
  }, [activeId, loadEventData]);

  // ─── HANDLERS ───
  const handleAuth = (userData) => { setUser(userData); setScreen("hub"); flash(`Welcome, ${userData.name}`); };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    setUser(null); setEvents([]); setActiveId(null); setActiveEvent(null);
    setScreen("auth");
  };

  const handleNameChange = (newName) => { setUser((u) => ({ ...u, name: newName })); };

  const handleCreateEvent = async (eventId) => {
    await supabase.from("event_members")
      .insert({ event_id: eventId, user_id: user.id, role: "member" });
    await loadEvents();
    setScreen("hub");
    flash("Event created!");
  };

  const handleJoinEvent = async (eventId) => {
    if (!eventId) { flash("Could not join — invalid event"); return; }
    await supabase.from("event_members")
      .insert({ event_id: eventId, user_id: user.id, role: "member" });
    setActiveId(eventId);
    const [{ data: ev }, { data: mem }, { data: dl }] = await Promise.all([
      supabase.from("events").select("*").eq("id", eventId).single(),
      supabase.from("event_members").select("user_id, role, profiles(name)").eq("event_id", eventId),
      supabase.from("drink_logs").select("*").eq("event_id", eventId).order("created_at", { ascending: false }),
    ]);
    if (!ev) { setActiveId(null); flash("Could not load event — try again"); return; }
    setActiveEvent(ev);
    setMembers((mem || []).map((m) => ({ user_id: m.user_id, role: m.role, name: m.profiles?.name || "Unknown" })));
    setLogs(dl || []);
    setScreen("log");
    flash(`Joined ${ev.name}!`);
    loadEvents(); // refresh hub list in background
  };

  const handleEnter = async (eventId) => {
    setActiveId(eventId);
    const [{ data: ev }, { data: mem }, { data: dl }] = await Promise.all([
      supabase.from("events").select("*").eq("id", eventId).single(),
      supabase.from("event_members").select("user_id, role, profiles(name)").eq("event_id", eventId),
      supabase.from("drink_logs").select("*").eq("event_id", eventId).order("created_at", { ascending: false }),
    ]);
    if (!ev) { setActiveId(null); setScreen("hub"); flash("Could not load event"); return; }
    setActiveEvent(ev);
    setMembers((mem || []).map((m) => ({ user_id: m.user_id, role: m.role, name: m.profiles?.name || "Unknown" })));
    setLogs(dl || []);
    setScreen("log");
  };
  const toHub = () => { setActiveId(null); setActiveEvent(null); setMembers([]); setLogs([]); setScreen("hub"); };

  const logDrink = async (oz, abv, type = "custom", targetUserId = null) => {
    if (!activeId || !user) return;
    // Hourly unit limit: 10 units per user per hour
    const targetId = targetUserId || user.id;
    const oneHourAgo = Date.now() - 3600000;
    const hourlyUnits = logs
      .filter((l) => l.user_id === targetId && new Date(l.created_at).getTime() > oneHourAgo)
      .reduce((s, l) => s + parseFloat(l.units), 0);
    const newUnits = calcSDU(oz, abv);
    if (hourlyUnits + newUnits > 15) {
      alert(`Cannot log — ${targetId === user.id ? "you've" : "this user has"} reached the 15 unit/hour limit (${hourlyUnits.toFixed(1)} units in the last hour)`);
      return;
    }
    const { error } = await supabase.from("drink_logs").insert({
      event_id: activeId,
      user_id: targetUserId || user.id,
      drink_type: type,
      oz,
      abv,
      logged_by: user.id,
    });
    if (error) { flash("Failed to log"); return; }
    flash(`+${calcSDU(oz, abv).toFixed(2)} units`);
    loadEventData();
  };

  const removeDrink = async (logId) => {
    await supabase.from("drink_logs").delete().eq("id", logId);
    flash("Drink removed");
    loadEventData();
  };

  // ─── COMPUTED ───
  const isAdmin = activeEvent?.created_by === user?.id;
  const leaderboard = members
    .map((m) => ({
      ...m,
      units: logs.filter((l) => l.user_id === m.user_id).reduce((s, l) => s + parseFloat(l.units), 0),
      isSelf: m.user_id === user?.id,
    }))
    .sort((a, b) => b.units - a.units);
  const totU = logs.reduce((s, l) => s + parseFloat(l.units), 0);
  const myU = logs.filter((l) => l.user_id === user?.id).reduce((s, l) => s + parseFloat(l.units), 0);
  const maxU = leaderboard.length > 0 ? Math.max(leaderboard[0].units, 0.1) : 0.1;
  const myLogs = logs.filter((l) => l.user_id === user?.id).slice(0, 5);
  const PRESET_SIGS = new Set(PRESETS.map((p) => `${p.oz}|${p.abv}`));
  const lastCustom = logs.find((l) => l.user_id === user?.id && !PRESET_SIGS.has(`${parseFloat(l.oz)}|${parseFloat(l.abv)}`)) || null;
  const otherMembers = members.filter((m) => m.user_id !== user?.id);

  // ─── LOADING ───
  if (screen === "loading") return <div style={{ minHeight: "100dvh", display: "flex", alignItems: "center", justifyContent: "center", background: "#0B0E13" }}><div style={{ fontSize: 44, animation: "pop 1.5s infinite" }}>🍻</div></div>;

  return (
    <div style={{ minHeight: "100dvh", background: "var(--bg)", color: "var(--text)", fontFamily: "var(--font)", maxWidth: 480, margin: "0 auto", position: "relative", paddingBottom: (screen === "log" || screen === "board") ? 90 : 0 }}>
      <style>{CSS}</style>
      <Toast msg={toast.msg} show={toast.show} />
      <CustomModal open={customOpen} onClose={() => { setCustomOpen(false); setCustomTarget(null); setCustomCategory("custom"); }} onLog={(oz, abv, cat) => logDrink(oz, abv, cat, customTarget)} initialCategory={customCategory} />
      {activeEvent && <ManageModal open={manageOpen} onClose={() => setManageOpen(false)} event={activeEvent} members={members} onSave={() => { loadEventData(); loadEvents(); flash("Updated"); }} userId={user?.id} onLeave={async () => {
        await supabase.from("event_members").delete().eq("event_id", activeId).eq("user_id", user.id);
        setManageOpen(false); setActiveId(null); setActiveEvent(null); setMembers([]); setLogs([]);
        await loadEvents(); setScreen("hub"); flash("Left event");
      }} onDelete={async () => {
        await supabase.from("drink_logs").delete().eq("event_id", activeId);
        await supabase.from("event_members").delete().eq("event_id", activeId);
        await supabase.from("events").delete().eq("id", activeId);
        setManageOpen(false); setActiveId(null); setActiveEvent(null); setMembers([]); setLogs([]);
        await loadEvents(); setScreen("hub"); flash("Event deleted");
      }} />}

      {screen === "auth" && <AuthScreen onAuth={handleAuth} />}
      {screen === "hub" && user && <EventHub user={user} events={events} onEnter={handleEnter} onCreate={() => setScreen("create")} onJoin={handleJoinEvent} onAccount={() => setScreen("account")} />}
      {screen === "account" && user && <AccountScreen user={user} onBack={() => setScreen("hub")} onLogout={handleLogout} onNameChange={handleNameChange} flash={flash} />}
      {screen === "create" && user && <CreateEvent user={user} onSave={handleCreateEvent} onBack={() => setScreen("hub")} />}

      {/* In-event header */}
      {(screen === "log" || screen === "board") && activeEvent && (
        <div style={{ padding: "16px 20px 0", animation: "fadeUp 0.3s ease" }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 14 }}>
            <button onClick={toHub} style={{ background: "none", border: "none", color: "var(--text3)", fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "var(--font)" }}>← Events</button>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <div style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, padding: "6px 12px" }}>
                <span style={{ fontSize: 11, fontWeight: 600, color: "var(--text3)", textTransform: "uppercase", letterSpacing: "0.06em" }}>Code </span>
                <span style={{ fontSize: 14, fontWeight: 800, color: "var(--amber)", letterSpacing: "0.15em" }}>{activeEvent.code}</span>
              </div>
              <button onClick={() => setManageOpen(true)} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, width: 36, height: 36, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", fontSize: 16, color: "var(--text2)" }}>⚙</button>
            </div>
          </div>
          <h1 style={{ fontSize: 22, fontWeight: 800, color: "var(--text)", textAlign: "center", letterSpacing: "-0.02em", marginBottom: 16 }}>{activeEvent.name}</h1>
          <div style={{ display: "flex", gap: 8 }}>
            <StatBox label="You" value={myU.toFixed(1)} color="var(--amber)" />
            <StatBox label="Total" value={totU.toFixed(1)} color="var(--purple)" />
            <StatBox label="People" value={members.length} color="var(--blue)" />
          </div>
        </div>
      )}

      {/* Quick Log */}
      {screen === "log" && activeEvent && (
        <div style={{ padding: "24px 20px", animation: "fadeUp 0.3s ease" }}>
          <SectionLabel>Quick Log</SectionLabel>
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* Beer button — expands to sub-presets */}
            <button onClick={() => setBeerOpen((o) => !o)} style={{ background: "var(--surface)", border: beerOpen ? "1px solid rgba(245,166,35,0.3)" : "1px solid var(--border)", borderRadius: 14, padding: "18px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14, width: "100%", transition: "all 0.15s", fontFamily: "var(--font)" }}
              onPointerDown={(e) => (e.currentTarget.style.transform = "scale(0.97)")}
              onPointerUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
              onPointerLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
            >
              <span style={{ fontSize: 28 }}>🍺</span>
              <div style={{ flex: 1, textAlign: "left" }}>
                <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Beer</div>
                <div style={{ fontSize: 13, color: "var(--text3)", fontWeight: 500, marginTop: 1 }}>Tap to choose</div>
              </div>
              <span style={{ color: "var(--text3)", fontSize: 18, transition: "transform 0.2s", transform: beerOpen ? "rotate(180deg)" : "none" }}>▾</span>
            </button>
            {beerOpen && (
              <div style={{ display: "flex", flexDirection: "column", gap: 8, paddingLeft: 12 }}>
                {BEER_PRESETS.map((bp) => (
                  <button key={bp.id} onClick={() => { logDrink(bp.oz, bp.abv, "beer"); setBeerOpen(false); }} style={{ background: "var(--surface2)", border: "1px solid var(--border)", borderRadius: 12, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", gap: 12, width: "100%", transition: "all 0.15s", fontFamily: "var(--font)" }}
                    onPointerDown={(e) => (e.currentTarget.style.transform = "scale(0.97)")}
                    onPointerUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
                    onPointerLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
                  >
                    <span style={{ fontSize: 22 }}>🍺</span>
                    <div style={{ flex: 1, textAlign: "left" }}>
                      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--text)" }}>{bp.label}</div>
                      <div style={{ fontSize: 12, color: "var(--text3)", fontWeight: 500 }}>{bp.oz} oz · {bp.abv}%</div>
                    </div>
                    <div style={{ fontSize: 14, fontWeight: 800, color: "var(--amber)", background: "rgba(245,166,35,0.08)", padding: "5px 10px", borderRadius: 8 }}>{calcSDU(bp.oz, bp.abv).toFixed(2)}u</div>
                  </button>
                ))}
                <button onClick={() => { setCustomCategory("beer"); setCustomOpen(true); setBeerOpen(false); }} style={{ background: "var(--surface2)", border: "1px dashed var(--border2)", borderRadius: 12, padding: "14px 16px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 8, width: "100%", fontFamily: "var(--font)", transition: "all 0.15s" }}>
                  <span style={{ fontSize: 18 }}>🍺</span><span style={{ fontSize: 14, fontWeight: 600, color: "var(--text2)" }}>Custom Beer</span>
                </button>
              </div>
            )}
            {/* Wine & Shot presets */}
            {PRESETS.filter((p) => p.id !== "beer").map((p) => (
              <button key={p.id} onClick={() => logDrink(p.oz, p.abv, p.id)} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "18px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14, width: "100%", transition: "all 0.15s", fontFamily: "var(--font)" }}
                onPointerDown={(e) => (e.currentTarget.style.transform = "scale(0.97)")}
                onPointerUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
                onPointerLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                <span style={{ fontSize: 28 }}>{p.emoji}</span>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)" }}>{p.label}</div>
                  <div style={{ fontSize: 13, color: "var(--text3)", fontWeight: 500, marginTop: 1 }}>{p.sub}</div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: "var(--amber)", background: "rgba(245,166,35,0.08)", padding: "6px 12px", borderRadius: 8 }}>{calcSDU(p.oz, p.abv).toFixed(2)}u</div>
              </button>
            ))}
            {lastCustom && (() => { const lcMeta = DRINK_META[lastCustom.drink_type] || DRINK_META.custom; return (
              <button onClick={() => logDrink(lastCustom.oz, lastCustom.abv, lastCustom.drink_type)} style={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 14, padding: "18px 20px", cursor: "pointer", display: "flex", alignItems: "center", gap: 14, width: "100%", transition: "all 0.15s", fontFamily: "var(--font)" }}
                onPointerDown={(e) => (e.currentTarget.style.transform = "scale(0.97)")}
                onPointerUp={(e) => (e.currentTarget.style.transform = "scale(1)")}
                onPointerLeave={(e) => (e.currentTarget.style.transform = "scale(1)")}
              >
                <span style={{ fontSize: 28 }}>{lcMeta.emoji}</span>
                <div style={{ flex: 1, textAlign: "left" }}>
                  <div style={{ fontSize: 17, fontWeight: 700, color: "var(--text)" }}>Last {lcMeta.label}</div>
                  <div style={{ fontSize: 13, color: "var(--text3)", fontWeight: 500, marginTop: 1 }}>{parseFloat(lastCustom.oz)}oz · {parseFloat(lastCustom.abv)}%</div>
                </div>
                <div style={{ fontSize: 15, fontWeight: 800, color: lcMeta.accent, background: `${lcMeta.accent}14`, padding: "6px 12px", borderRadius: 8 }}>{calcSDU(lastCustom.oz, lastCustom.abv).toFixed(2)}u</div>
              </button>
            ); })()}
            <button onClick={() => setCustomOpen(true)} style={{ background: "var(--surface)", border: "1px dashed var(--border2)", borderRadius: 14, padding: "18px 20px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 10, width: "100%", fontFamily: "var(--font)", transition: "all 0.15s" }}>
              <span style={{ fontSize: 22 }}>🍹</span><span style={{ fontSize: 16, fontWeight: 600, color: "var(--text2)" }}>Custom Drink</span>
            </button>
          </div>

          {otherMembers.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <SectionLabel>Log for Others</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {otherMembers.map((m) => (
                  <LogOther key={m.user_id} participant={m} onLog={(oz, abv, type, uid) => logDrink(oz, abv, type, uid)} onCustom={(uid) => { setCustomTarget(uid); setCustomOpen(true); }} />
                ))}
              </div>
            </div>
          )}

          {myLogs.length > 0 && (
            <div style={{ marginTop: 28 }}>
              <SectionLabel>Your Recent</SectionLabel>
              <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                {myLogs.map((l) => {
                  const meta = DRINK_META[l.drink_type] || DRINK_META.custom;
                  return (
                    <div key={l.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 14px", background: "var(--surface)", borderRadius: 10, border: "1px solid var(--border)", fontSize: 14 }}>
                      <span style={{ color: "var(--text)" }}>{meta.emoji} +{parseFloat(l.units).toFixed(2)}u</span>
                      <span style={{ color: "var(--text3)", fontSize: 12 }}>{fmtAgo(l.created_at)}</span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Leaderboard */}
      {screen === "board" && activeEvent && (
        <div style={{ padding: "24px 20px", animation: "fadeUp 0.3s ease" }}>
          <SectionLabel>Leaderboard</SectionLabel>
          {leaderboard.length === 0 ? (
            <div style={{ textAlign: "center", padding: 40, color: "var(--text3)", fontSize: 15, fontWeight: 500 }}>No drinks logged yet</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {leaderboard.map((u, i) => (
                <LBRow key={u.user_id} rank={i} name={u.name} units={u.units} isSelf={u.isSelf} max={maxU} onClick={() => { setSelUserId(u.user_id); setScreen("userDetail"); }} />
              ))}
            </div>
          )}
        </div>
      )}

      {/* User Detail */}
      {screen === "userDetail" && activeId && selUserId && (
        <UserDetail
          userId={selUserId}
          eventId={activeId}
          currentUserId={user?.id}
          isAdmin={isAdmin}
          onBack={() => { setSelUserId(null); setScreen("board"); }}
          onDrinkRemoved={loadEventData}
          flash={flash}
        />
      )}

      {/* Bottom Nav */}
      {(screen === "log" || screen === "board") && activeEvent && (
        <div style={{ position: "fixed", bottom: 0, left: "50%", transform: "translateX(-50%)", width: "100%", maxWidth: 480, display: "flex", background: "linear-gradient(180deg, transparent 0%, var(--bg) 30%)", padding: "20px 20px 24px", gap: 10, zIndex: 100 }}>
          {[{ id: "log", label: "Log", icon: "⚡" }, { id: "board", label: "Board", icon: "🏆" }].map((t) => (
            <button key={t.id} onClick={() => setScreen(t.id)} style={{
              flex: 1, padding: "14px 0", borderRadius: 12, fontSize: 14, fontWeight: 700,
              fontFamily: "var(--font)", cursor: "pointer", transition: "all 0.2s", letterSpacing: "-0.01em",
              background: screen === t.id ? "var(--surface2)" : "transparent",
              color: screen === t.id ? "var(--text)" : "var(--text3)",
              border: screen === t.id ? "1px solid var(--border2)" : "1px solid transparent",
            }}>{t.icon} {t.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
